"use client";

import { useEffect, useMemo, useState } from "react";
import { Bug, CheckCircle2, ClipboardCopy, Eraser, PlayCircle, TriangleAlert } from "lucide-react";
import {
  buildDiagnosticReport,
  clearDiagnostics,
  readDiagnostics,
  recordDiagnostic,
  subscribeDiagnostics,
  type DiagnosticRange,
} from "@/lib/aqua/diagnostics";
import type { AquaScreenContext } from "@/lib/aqua/types";

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const area = document.createElement("textarea");
  area.value = value;
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  document.execCommand("copy");
  area.remove();
}

export function DiagnosticsView({ getContext }: { getContext: () => AquaScreenContext }) {
  const [range, setRange] = useState<DiagnosticRange>("today");
  const [revision, setRevision] = useState(0);
  const [notice, setNotice] = useState("");

  useEffect(() => subscribeDiagnostics(() => setRevision((value) => value + 1)), []);
  const entries = useMemo(() => readDiagnostics(range), [range, revision]);
  const report = useMemo(() => buildDiagnosticReport(entries, range), [entries, range]);
  const failures = entries.filter((entry) => entry.level === "FAIL").length;
  const limitations = entries.filter((entry) => entry.level === "LIMITATION").length;
  const passes = entries.filter((entry) => entry.level === "PASS").length;

  const runDiagnostic = () => {
    const context = getContext();
    const controls = context.visibleControls.length;
    const fields = context.writableFields.length;
    const online = navigator.onLine;
    recordDiagnostic({
      level: online ? "PASS" : "FAIL",
      category: "network",
      event: "diagnostic.connectivity",
      message: online ? "Device reports an active network connection." : "Device reports that it is offline.",
      expected: "online=true",
      actual: `online=${online}`,
      view: context.view,
    });
    recordDiagnostic({
      level: controls > 0 ? "PASS" : "FAIL",
      category: "operator",
      event: "diagnostic.semantic-map",
      message: `Measured ${controls} visible registered controls and ${fields} writable fields.`,
      expected: "At least one visible registered control.",
      actual: `controls=${controls}; fields=${fields}; atTop=${context.viewport.atTop}; atBottom=${context.viewport.atBottom}`,
      view: context.view,
    });
    recordDiagnostic({
      level: "INFO",
      category: "aqua",
      event: "diagnostic.archive.generated",
      message: `Diagnostic archive refreshed for ${range}. Physical voice remains NOT TESTED until reviewed on the device.`,
      view: context.view,
    });
    setNotice("Diagnostic run archived.");
    window.setTimeout(() => setNotice(""), 2200);
  };

  const copyReport = async () => {
    await copyText(buildDiagnosticReport(readDiagnostics(range), range));
    setNotice("Diagnostic report copied for reviewing.");
    window.setTimeout(() => setNotice(""), 2200);
  };

  const erase = () => {
    if (!window.confirm("Clear the saved diagnostic history on this device?")) return;
    clearDiagnostics();
    setRevision((value) => value + 1);
    setNotice("Diagnostic history cleared.");
  };

  return <section className="diagnostics-page" data-aqua-scroll="diagnostics">
    <div className="manager-head">
      <div><span className="eyebrow">Development Evidence</span><h1>Diagnostics</h1></div>
      <button data-aqua-id="run-diagnostics" className="primary manager-button" onClick={runDiagnostic}><PlayCircle size={17}/>Run Diagnostic</button>
    </div>

    {notice ? <div className="message" role="status">{notice}</div> : null}

    <div className="diagnostic-range" role="group" aria-label="Diagnostic time range">
      <button data-aqua-id="diagnostics-range-today" className={range === "today" ? "active" : ""} onClick={() => setRange("today")}>Today</button>
      <button data-aqua-id="diagnostics-range-seven-days" className={range === "seven_days" ? "active" : ""} onClick={() => setRange("seven_days")}>7 Days</button>
      <button data-aqua-id="diagnostics-range-all" className={range === "all" ? "active" : ""} onClick={() => setRange("all")}>All</button>
    </div>

    <div className="diagnostic-metrics">
      <div><CheckCircle2/><span>Verified passes</span><strong>{passes}</strong></div>
      <div><TriangleAlert/><span>Failures</span><strong>{failures}</strong></div>
      <div><Bug/><span>Limitations</span><strong>{limitations}</strong></div>
    </div>

    <div className="panel diagnostic-actions">
      <div><h2>Developer-ready report</h2><p>Includes requests, semantic actions, expected versus actual results, errors, limitations, device details, and timestamps.</p></div>
      <button data-aqua-id="copy-diagnostics" className="secondary-button" onClick={copyReport}><ClipboardCopy size={16}/>Copy diagnostics for reviewing</button>
      <button data-aqua-id="clear-diagnostic-history" data-aqua-consequence="destructive" className="text-danger-button" onClick={erase}><Eraser size={15}/>Clear Diagnostic History</button>
    </div>

    <div className="diagnostic-log">
      <div className="section-head"><h2>Archived Events</h2><span>{entries.length}</span></div>
      {entries.length ? <div className="stack">{[...entries].reverse().slice(0, 250).map((entry) => <article className={`card diagnostic-entry ${entry.level.toLowerCase()}`} key={entry.id}>
        <div><strong>{entry.level} · {entry.event}</strong><time>{new Date(entry.timestamp).toLocaleString()}</time></div>
        <p>{entry.message}</p>
        {entry.action || entry.target ? <small>{entry.action ? `Action: ${entry.action}` : ""}{entry.action && entry.target ? " · " : ""}{entry.target ? `Target: ${entry.target}` : ""}</small> : null}
        {entry.expected ? <small>Expected: {entry.expected}</small> : null}
        {entry.actual ? <small>Actual: {entry.actual}</small> : null}
      </article>)}</div> : <div className="panel diagnostic-empty"><Bug size={24}/><p>No events in this range. Tap Run Diagnostic or use Aqua to begin collecting evidence.</p></div>}
    </div>

    <details className="diagnostic-raw"><summary>Report preview</summary><pre>{report}</pre></details>
  </section>;
}
